import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { useAPI } from 'hooks/api/useAPI';

import APIError from 'types/api_error';
import Pipeline from 'types/pipeline';

import { updateQueriesData } from 'services/update_queries_data';

export function useDeletePipelines({
  onMutate,
  onError,
  onSettled,
  ...options
}: UseMutationOptions<void, APIError, { pipeline_ids: string[] }> = {}) {
  const queryClient = useQueryClient();
  const { applicationID, client } = useAPI();

  return useMutation(
    async ({ pipeline_ids }) => {
      await Promise.all(
        pipeline_ids.map((id) =>
          client.delete(`/apps/${applicationID}/pipelines/${id}`)
        )
      );
    },
    {
      ...options,
      async onMutate({ pipeline_ids }) {
        await queryClient.cancelQueries(['pipelines']);
        await queryClient.cancelQueries(['pipeline']);

        onMutate?.({ pipeline_ids });

        return updateQueriesData<Pipeline>({
          queryClient,
          listQueryKey: ['pipelines'],
          singleQueryKey: ['pipeline'],
          ids: pipeline_ids,
          updateData: null,
        });
      },
      onError(error, params, context) {
        onError?.(error, params, context);

        context?.previousLists?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
        context?.previousSingles?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
      },
      async onSettled(...args) {
        onSettled?.(...args);

        queryClient.invalidateQueries(['pipelines']);
        queryClient.invalidateQueries(['pipeline']);
      },
    }
  );
}
